import { NotFound } from "@app/shared/ErrorPages";
import { JSX, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { focusFirstInteractiveElement } from "@app/shared/utility/focusFirstInteractiveElement";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageInner from "@app/shared/PageInner";
import { UserContext } from "@app/user/UserContext";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import { WizardNavigationBar } from "@app/shared/qes-standards/WizardNavigationBar";
import { WizardSteps } from "@app/shared/qes-standards/WizardSteps";
import Roles from "@app/user/roles";
import { AccuracyReportType } from "@app/reports/accuracy-reports/accuracyReportType";
import { AccuracyReportsProvider, AccuracyReportsContext } from "@app/reports/accuracy-reports/import/AccuracyReportsContext";
import { AccuracyReportsWizardContextSteps } from "@app/reports/accuracy-reports/import/interfaces";
import AccuracyReportsConfig from "@app/reports/accuracy-reports/steps/AccuracyReportsConfig";
import AccuracyReportsSelectActionableProviders from "@app/reports/accuracy-reports/steps/AccuracyReportsSelectActionableProviders";
import AccuracyReportsSelectProjects from "@app/reports/accuracy-reports/steps/AccuracyReportsSelectProjects";

const AccuracyReportsWizard = (): JSX.Element => {
  const { reportType } = useParams<{ reportType: AccuracyReportType }>();
  const user = useContext(UserContext);

  if (!["actionable-provider"].includes(reportType) || !user.roles.includes(Roles.ACCURACY_LICENSE_TIER_2)) {
    return <NotFound />;
  }

  return (
    <AccuracyReportsProvider reportType={reportType}>
      <AccuracyReportsWizardContent />
    </AccuracyReportsProvider>
  );
};

const AccuracyReportsWizardContent = (): JSX.Element => {
  const wizardContext = useContext(AccuracyReportsContext);
  const wizardBodyRef = useRef<HTMLDivElement>(null);
  const [pageIsValid, setPageIsValid] = useState(false);
  const history = useHistory();

  useEffect(() => {
    if (!wizardContext.loading) {
      focusFirstInteractiveElement(wizardBodyRef);
    }
  }, [wizardContext.page, wizardContext.loading]);

  const pageTitle = useRef(() => {
    switch (wizardContext.reportType) {
      case "actionable-provider":
        return "Actionable Provider Reports";
    }
  }).current();

  const getHumanReadablePageNames = (): string[] => {
    return ["Projects", "Actionable Providers", "Configure"];
  };

  const getCurrentStep = (): number => {
    switch (wizardContext.page) {
      case AccuracyReportsWizardContextSteps.SelectProjects:
        return 0;
      case AccuracyReportsWizardContextSteps.SelectActionableProviders:
        return 1;
      case AccuracyReportsWizardContextSteps.Configure:
        return 2;
      default:
        return 0;
    }
  };

  const handleUpdatePage = (newPage: number): void => {
    const steps = [
      AccuracyReportsWizardContextSteps.SelectProjects,
      AccuracyReportsWizardContextSteps.SelectActionableProviders,
      AccuracyReportsWizardContextSteps.Configure,
    ];
    if (newPage >= 0 && newPage < steps.length) {
      wizardContext.goToStep(steps[newPage]);
    }
  };

  const currentWizardStep = useMemo(() => {
    switch (wizardContext.page) {
      case AccuracyReportsWizardContextSteps.SelectProjects:
        return <AccuracyReportsSelectProjects pageIsValid={setPageIsValid} />;
      case AccuracyReportsWizardContextSteps.SelectActionableProviders:
        return <AccuracyReportsSelectActionableProviders setPageIsValid={setPageIsValid} />;
      case AccuracyReportsWizardContextSteps.Configure:
        return <AccuracyReportsConfig setPageIsValid={setPageIsValid} />;
      default:
        return <NotFound />;
    }
  }, [wizardContext.page]);

  const goToPreviousPage = (): void => {
    wizardContext.goToPreviousStep();
  };

  const validateAndAdvance = (): void => {
    wizardContext.goToNextStep();
  };

  const handleGenerate = async (): Promise<void> => {
    await wizardContext.generateReports?.();
  };

  // Handle redirect after successful generation
  useEffect(() => {
    if (wizardContext.success && !wizardContext.error) {
      history.push("/reports");
    }
  }, [wizardContext.success, wizardContext.error, history]);

  return (
    <Page documentTitle={pageTitle}>
      <PageInner className="ar__wizard">
        <PageHeader title={pageTitle} disableHR={true}>
        </PageHeader>
        <div className="d-flex align-items-center justify-content-between flex-nowrap">
          <div className="flex-shrink-0">
            <WizardSteps currentStep={getCurrentStep()} steps={getHumanReadablePageNames()} setStep={handleUpdatePage} />
          </div>
          {wizardContext.showSettingsPartialApplyWarning && wizardContext.page === AccuracyReportsWizardContextSteps.SelectActionableProviders && (
            <QESAlert type="warning" classNames="mb-0 ms-3" onClose={() => wizardContext.setShowSettingsPartialApplyWarning?.(false)}>
              Only filters shown on this page have been applied. Filters from other areas have not been applied.
            </QESAlert>
          )}
        </div>
        <hr />
        {wizardContext.error && (
          <QESAlert type="danger" persist={true} classNames="mb-3">{wizardContext.error}</QESAlert>
        )}
        <div ref={wizardBodyRef}>
          {wizardContext.loading ? (
            <LoadingSpinner />
          ) : (
            currentWizardStep
          )}
        </div>
      </PageInner>
      <WizardNavigationBar
        leftSide={(
          <Link to="/reports" className="qes-button qes-button-secondary danger" data-testid="ar__cancel">Cancel</Link>
        )}
        rightSide={(
          <div className="accuracy-reports__nav-buttons">
            {wizardContext.page !== AccuracyReportsWizardContextSteps.SelectProjects && (
              <QESButton qesType="secondary" onClick={goToPreviousPage} qesTestId="accuracy-reports__previous">Previous</QESButton>
            )}
            {wizardContext.page === AccuracyReportsWizardContextSteps.Configure ? (
              <QESButton disabled={!pageIsValid || wizardContext.processing} qesType="primary" qesTestId="accuracy-reports__generate" className="ms-3"onClick={handleGenerate}>
                Generate
              </QESButton>
            ) : (
              <QESButton onClick={validateAndAdvance} disabled={!pageIsValid} qesTestId="accuracy-reports__next" className="ms-3">Next</QESButton>
            )}
          </div>
        )}
      />
    </Page>
  );
};

export default AccuracyReportsWizard;
